import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Mail, Loader2, ShieldCheck, Clock, MessageSquare } from "lucide-react";

const SUPPORT_EMAIL = "support@windowscreenquote.com";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "General inquiry", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/public/contact`, form);
      setSent(true);
      toast.success("Thanks — we'll be in touch shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send. Please email us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBFAF6]">
      <nav className="px-5 sm:px-8 lg:px-12 py-4 flex items-center justify-between border-b border-slate-200/60 bg-white/80 backdrop-blur sticky top-0 z-40">
        <Link to="/" className="flex items-center gap-2.5"><Logo size={36} withWordmark /></Link>
        <Link to="/" className="text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid="contact-back-link">Back to home</Link>
      </nav>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-14 lg:py-20 grid lg:grid-cols-12 gap-10" data-testid="contact-page">
        <section className="lg:col-span-5">
          <div className="text-xs font-bold uppercase tracking-widest text-[#C75D33] mb-3">Contact</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight leading-[1.05]">We're here to help.</h1>
          <p className="text-slate-600 mt-4 text-base leading-relaxed">
            Questions about a quote, your screen company account, billing, or our SMS messaging program? Send us a note and we'll get back to you. Most messages are answered within one business day.
          </p>

          <ul className="mt-8 space-y-4">
            <ContactRow icon={Mail} label="Support email">
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#C75D33] font-semibold hover:underline" data-testid="contact-support-email">{SUPPORT_EMAIL}</a>
              <div className="text-xs text-slate-500 mt-0.5">Best for billing, account, or SMS opt-out questions.</div>
            </ContactRow>
            <ContactRow icon={Clock} label="Response time">
              <div className="text-slate-900 font-semibold">~1 business day</div>
              <div className="text-xs text-slate-500 mt-0.5">Mon–Fri, 9a–6p Pacific.</div>
            </ContactRow>
            <ContactRow icon={MessageSquare} label="SMS opt-out">
              <div className="text-slate-900 font-semibold">Reply STOP to any of our texts.</div>
              <div className="text-xs text-slate-500 mt-0.5">Reply HELP for help. Msg &amp; data rates may apply. <Link to="/privacy" className="underline">Privacy Policy</Link>.</div>
            </ContactRow>
            <ContactRow icon={ShieldCheck} label="Mailing address">
              <div className="text-slate-900">WindowScreenQuote.com<br />PO Box pending<br />United States</div>
            </ContactRow>
          </ul>
        </section>

        <aside className="lg:col-span-7">
          <div className="bg-white rounded-3xl shadow-xl shadow-slate-900/5 border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-br from-[#0F172A] to-black text-white p-6 sm:p-8">
              <div className="text-xs font-bold uppercase tracking-widest text-white/85 mb-1.5">Send us a message</div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold leading-tight">We read every note.</h2>
            </div>
            {sent ? (
              <div className="p-8 sm:p-10 text-center" data-testid="contact-thanks">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3">
                  <Mail className="w-6 h-6" />
                </div>
                <h3 className="font-display text-xl font-bold text-slate-900">Thanks — we got your message.</h3>
                <p className="text-slate-600 mt-2 text-sm leading-relaxed">
                  We'll get back to you at <strong>{form.email}</strong> within one business day. If it's urgent,
                  email <a className="text-[#C75D33] hover:underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> directly.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="p-6 sm:p-8 space-y-4" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Your name">
                    <Input required value={form.name} onChange={set("name")} className="h-11 rounded-xl" data-testid="contact-name-input" placeholder="Jane Smith" />
                  </Field>
                  <Field label="Email">
                    <Input required type="email" value={form.email} onChange={set("email")} className="h-11 rounded-xl" data-testid="contact-email-input" placeholder="you@example.com" />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Phone (optional)">
                    <Input value={form.phone} onChange={set("phone")} className="h-11 rounded-xl" data-testid="contact-phone-input" placeholder="(555) 555-1234" />
                  </Field>
                  <Field label="Subject">
                    <select
                      value={form.subject}
                      onChange={set("subject")}
                      className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                      data-testid="contact-subject-select"
                    >
                      <option>General inquiry</option>
                      <option>Question about a quote</option>
                      <option>Screen company account</option>
                      <option>Billing</option>
                      <option>SMS / text messaging</option>
                      <option>Privacy / data request</option>
                      <option>Other</option>
                    </select>
                  </Field>
                </div>
                <Field label="Message">
                  <Textarea
                    required
                    value={form.message}
                    onChange={set("message")}
                    rows={6}
                    className="rounded-xl"
                    data-testid="contact-message-input"
                    placeholder="How can we help?"
                  />
                </Field>
                <Button
                  type="submit"
                  size="lg"
                  disabled={loading}
                  className="w-full h-12 rounded-2xl bg-slate-900 hover:bg-black text-white text-sm font-bold shadow-lg shadow-slate-900/25"
                  data-testid="contact-submit-btn"
                >
                  {loading ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sending…</> : "Send message"}
                </Button>
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  By submitting, you agree to our <Link to="/terms" className="underline">Terms</Link> and <Link to="/privacy" className="underline">Privacy Policy</Link>. We'll only use your details to reply to this message.
                </p>
              </form>
            )}
          </div>
        </aside>
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function ContactRow({ icon: Icon, label, children }) {
  return (
    <li className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-xl bg-[#C75D33]/10 text-[#C75D33] flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
        <div className="mt-0.5">{children}</div>
      </div>
    </li>
  );
}
